import { GUEST_PATH_SILENCE_MS } from './webrtcLatencyContracts';

export const RECOVER_PATH_MESSAGE = { type: 'recoverPath' } as const;

export function isRecoverPathMessage(data: string): boolean {
  try {
    return (JSON.parse(data) as { type?: unknown }).type === RECOVER_PATH_MESSAGE.type;
  } catch {
    return false;
  }
}

export type PathSilenceVerdict = 'healthy' | 'silent' | 'no-media';

/**
 * One inbound-video sample. `sourceId` is the stream identity the counter belongs to
 * (`GuestStatsSample.decodeSample.sourceId`, which encodes the stat id and SSRC). A change
 * means the stream was replaced, so the counter's value is not comparable to the last one.
 */
export type PathSilenceSample = {
  sourceId: string;
  framesDecoded: number;
  nowMs: number;
};

/**
 * Decides when a path that still looks connected has actually stopped carrying video.
 * A radio change leaves ICE `connected` and loss at 0; the missing signal is frames.
 *
 * The counter is cumulative PER STREAM, so the watch is keyed on the stream identity. A
 * same-connection stream replacement restarts `framesDecoded` at zero; reading that as "no
 * progress" used to declare `silent` three seconds later and restart ICE against a path that
 * was delivering media. A decrease (or an identity change) is a baseline reset, never silence.
 */
export class GuestPathSilenceWatch {
  private lastFrames: number | null = null;
  private lastSourceId: string | null = null;
  private lastProgressAtMs: number | null = null;
  private awaitingFirstFrame = false;
  private awaitingSinceMs: number | null = null;
  private fired = false;
  /** The armed gap timer's generation; bumping it disarms without a new timeout. */
  private gapArmed = false;

  /** A reclaim may connect the data channel and still never send video. */
  armAwaitingMedia(): void {
    this.awaitingFirstFrame = true;
    this.awaitingSinceMs = null;
    this.fired = false;
    this.gapArmed = false;
  }

  /** Present counter and identity; a change re-baselines the watch without declaring silence. */
  noteFrames(sample: PathSilenceSample): PathSilenceVerdict;
  /** Compatibility overload for callers without a stream identity. */
  noteFrames(framesDecoded: number, nowMs: number): PathSilenceVerdict;
  noteFrames(sampleOrFrames: PathSilenceSample | number, nowMs?: number): PathSilenceVerdict {
    const sample: PathSilenceSample = typeof sampleOrFrames === 'number'
      ? { sourceId: this.lastSourceId ?? '', framesDecoded: sampleOrFrames, nowMs: nowMs ?? 0 }
      : sampleOrFrames;

    // A different stream: the counter is unrelated to the previous one, so treat this as a
    // fresh baseline (progress if it has decoded anything) rather than as a stall.
    if (sample.sourceId !== '' && this.lastSourceId !== null && sample.sourceId !== this.lastSourceId) {
      this.lastSourceId = sample.sourceId;
      return this.markProgress(sample.framesDecoded, sample.nowMs);
    }
    this.lastSourceId = sample.sourceId === '' ? this.lastSourceId : sample.sourceId;

    // A DECREASE is a baseline reset (the reader restarted the counter without changing the
    // identity), not silence.
    if (this.lastFrames !== null && sample.framesDecoded < this.lastFrames) {
      return this.markProgress(sample.framesDecoded, sample.nowMs);
    }
    if (this.lastFrames !== null && sample.framesDecoded > this.lastFrames) {
      return this.markProgress(sample.framesDecoded, sample.nowMs);
    }
    if (this.lastFrames === null && sample.framesDecoded > 0) {
      return this.markProgress(sample.framesDecoded, sample.nowMs);
    }

    this.lastFrames = sample.framesDecoded;
    if (this.fired) return 'healthy';
    if (this.awaitingFirstFrame) {
      if (this.awaitingSinceMs === null) this.awaitingSinceMs = sample.nowMs;
      if (sample.nowMs - this.awaitingSinceMs >= GUEST_PATH_SILENCE_MS) {
        this.fired = true;
        return 'no-media';
      }
      return 'healthy';
    }
    if (this.lastProgressAtMs !== null && sample.nowMs - this.lastProgressAtMs >= GUEST_PATH_SILENCE_MS) {
      this.fired = true;
      return 'silent';
    }
    return 'healthy';
  }

  /** getStats hung after video had been flowing. No sample is itself the silence. */
  notePollGap(nowMs: number): PathSilenceVerdict {
    if (this.fired || this.lastProgressAtMs === null) return 'healthy';
    if (nowMs - this.lastProgressAtMs < GUEST_PATH_SILENCE_MS) return 'healthy';
    this.fired = true;
    return 'silent';
  }

  hasSeenProgress(): boolean {
    return this.lastProgressAtMs !== null;
  }

  /** Last framesDecoded sample, or null before the first noteFrames. */
  lastFramesDecoded(): number | null {
    return this.lastFrames;
  }

  hasRecentProgress(nowMs: number): boolean {
    return this.lastProgressAtMs !== null && nowMs - this.lastProgressAtMs < GUEST_PATH_SILENCE_MS;
  }

  /**
   * True once per stall: the caller must arm the gap timer for THIS stall and not re-arm on
   * every healthy tick. A previous bug re-armed the timer once a second forever after one
   * fire, so no second verdict could ever be produced.
   */
  claimGapArm(): boolean {
    if (this.gapArmed || this.fired || this.lastProgressAtMs === null) return false;
    this.gapArmed = true;
    return true;
  }

  /** Release the gap arm so a later stall can claim it again. */
  releaseGapArm(): void {
    this.gapArmed = false;
  }

  /** Re-baseline for a new stream generation without awaiting a frame. */
  resetForPath(): void {
    this.lastFrames = null;
    this.lastSourceId = null;
    this.lastProgressAtMs = null;
    this.awaitingFirstFrame = false;
    this.awaitingSinceMs = null;
    this.fired = false;
    this.gapArmed = false;
  }

  reset(): void {
    this.resetForPath();
  }

  private markProgress(framesDecoded: number, nowMs: number): PathSilenceVerdict {
    this.lastFrames = framesDecoded;
    this.lastProgressAtMs = nowMs;
    this.awaitingFirstFrame = false;
    this.awaitingSinceMs = null;
    this.fired = false;
    this.gapArmed = false;
    return 'healthy';
  }
}
